/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — a variant's pending master updates, painted IN the
   document with the Review engine (Tweaks 35).

   A master publish reaches a variant as offers (shared/variant-updates.ts,
   held at publish). The Updates panel lists them; this hook paints the same
   offers on the sheet with the Review plugin the project valve uses:
   OURS = the variant as it reads on screen (held, old words), THEIRS = the
   same variant with every pending offer accepted. Only blocks carrying a
   pending offer are painted. Decorations only — the document is untouched.
   `items` = one stop per pending block for the Review bar; its Accept /
   Reject / Accept all run the SAME decide as the Updates panel and the
   block menu (Studio wires them to `updates.decideBlock` / `decideAll`).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor, JSONContent } from '@tiptap/react';
import { merge3, type Change } from '../../shared/sync';
import type { ClientLayer, ContentBlock } from '../../shared/types';
import {
  applyDecisionToLayer,
  isModuleEntry,
  type VariantChangeEntry,
} from '../../shared/variant-updates';
import { docToBlocks } from '../designer/schema';
import { composeVariant, isGhostNode } from './variantOps';

/** The variant with every pending master-publish offer accepted — what it
 *  would read after "Accept all". Pure. */
export function acceptedLayer(layer: ClientLayer, entries: VariantChangeEntry[]): ClientLayer {
  let next = layer;
  for (const entry of entries || []) {
    if (isModuleEntry(entry) || entry.local || entry.layerId !== layer.id) continue;
    for (const change of entry.changes || []) {
      if (change.decision !== 'pending') continue;
      next = applyDecisionToLayer(next, change, 'accepted');
    }
  }
  return next;
}

export interface VariantReview {
  /** What the Review plugin paints. */
  paint: Change[];
  /** One stop per pending block (an offer is decided per block). */
  items: Change[];
}

/** The Review changes for one variant: on-screen blocks against the
 *  accepted composition, kept to the blocks with a pending offer. Pure. */
export function variantReviewChanges(args: {
  onScreen: ContentBlock[];
  publishedCore: ContentBlock[];
  masterId: string;
  layer: ClientLayer;
  entries: VariantChangeEntry[];
  pendingIds: Set<string>;
  /** Blocks already on the sheet as a ghost row (a held-out new block): the
   *  ghost row IS its mark, so the Review ghost is not drawn twice. */
  ghostIds?: Set<string>;
  /** Every block id in sheet order (ghost rows included): the bar walks the
   *  document top to bottom. */
  order?: string[];
}): VariantReview {
  if (!args.pendingIds.size) return { paint: [], items: [] };
  const ghosts = args.ghostIds || new Set<string>();
  const theirs = composeVariant(
    args.publishedCore,
    args.masterId,
    acceptedLayer(args.layer, args.entries)
  )
    .blocks.filter((c) => !c.hidden)
    .map((c) => c.block);
  const all = merge3(args.onScreen, args.onScreen, theirs).filter(
    (c) => c.kind !== 'reconciled' && args.pendingIds.has(c.blockId)
  );
  const seen = new Set<string>();
  const at = new Map((args.order || []).map((id, i) => [id, i]));
  const items = all
    .filter((c) => (seen.has(c.blockId) ? false : (seen.add(c.blockId), true)))
    .map((c, i) => ({ c, i, pos: at.get(c.blockId) ?? Number.MAX_SAFE_INTEGER }))
    .sort((x, y) => x.pos - y.pos || x.i - y.i)
    .map((x) => x.c);
  const paint = all.filter((c) => !(c.whole && c.kind === 'added' && ghosts.has(c.blockId)));
  return { paint, items };
}

const visibleBlocks = (json: JSONContent): ContentBlock[] =>
  docToBlocks({ ...json, content: (json.content || []).filter((n) => !isGhostNode(n)) });
const ghostIdsOf = (json: JSONContent): Set<string> =>
  new Set(
    (json.content || [])
      .filter((n) => isGhostNode(n))
      .map((n) => String((n.attrs as { blockId?: string } | undefined)?.blockId || ''))
  );

export function useVariantReview(args: {
  editor: Editor | null;
  masterId: string;
  publishedCore: ContentBlock[] | undefined;
  layer: ClientLayer | null;
  entries: VariantChangeEntry[];
  pendingIds: Set<string>;
  /** Bumps whenever the feed moves. */
  version: number;
}): VariantReview | null {
  const { editor, masterId, publishedCore, layer, entries, pendingIds, version } = args;
  // The sheet moves under the paint as the variant is edited: re-read it a
  // beat after typing stops so word offsets keep indexing what is on screen.
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!editor || editor.isDestroyed || !layer || !pendingIds.size) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const onUpdate = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => setTick((n) => n + 1), 400);
    };
    editor.on('update', onUpdate);
    return () => {
      if (t) clearTimeout(t);
      editor.off('update', onUpdate);
    };
  }, [editor, layer, pendingIds]);

  return React.useMemo<VariantReview | null>(() => {
    if (!editor || editor.isDestroyed || !layer || !publishedCore || !pendingIds.size) return null;
    const json = editor.getJSON();
    const review = variantReviewChanges({
      onScreen: visibleBlocks(json),
      ghostIds: ghostIdsOf(json),
      order: (json.content || []).map((n) =>
        String((n.attrs as { blockId?: string } | undefined)?.blockId || '')
      ),
      publishedCore,
      masterId,
      layer,
      entries,
      pendingIds,
    });
    return review.items.length ? review : null;
    // `version` / `tick` are the re-read triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, layer, publishedCore, masterId, entries, pendingIds, version, tick]);
}
